import { Config } from "@configs/config";
import { TraceLevel } from "@enums/trace-level.enum";
import { getSessionId } from "@session/session";

export abstract class BaseTracer {

    private static traceLevelLength: number = 5;

    public error(message: string, ...optionalParams: any[]): void {
        this.trace(TraceLevel.Error, message, optionalParams);
    }

    public warn(message: string, ...optionalParams: any[]): void {
        this.trace(TraceLevel.Warn, message, optionalParams);
    }

    public info(message: string, ...optionalParams: any[]): void {
        this.trace(TraceLevel.Info, message, optionalParams);
    }

    public debug(message: string, ...optionalParams: any[]): void {
        this.trace(TraceLevel.Debug, message, optionalParams);
    }

    public verbose(message: string, ...optionalParams: any[]): void {
        this.trace(TraceLevel.Verbose, message, optionalParams);
    }

    public logPlatformDetails() {

        try {

            this.info('Platform: ' + Office?.context?.diagnostics?.platform);
            this.info('HostName: ' + Office?.context?.mailbox?.diagnostics?.hostName);
            this.info('HostVersion: ' + Office?.context?.mailbox?.diagnostics?.hostVersion);
            this.info('AIRVVersion: ' + Config.version);
        } catch (err) {
            this.info("Error while retrieving platform data", err);
        }
    }

    protected trace(traceLevel: TraceLevel, message: string, optionalParams?: any[]): void {
        try {
            const trace = this.getTrace(traceLevel, message, optionalParams);
            console.log(trace);
            this.storeTrace(trace);
        } catch (ex) {
            console.warn(`Error while tracing message '${message}'`, ex);
            // Swallow.
        }
    }

    protected abstract storeTrace(trace: string): void;

    private getTrace(traceLevel: TraceLevel, message: string, optionalParams?: any[]): string {
        var trace = `[${new Date().toISOString()} ${this.getFormattedTraceLevel(traceLevel)} ${this.getFormattedSessionId()}] ${message}`;

        const traceDetail = this.getTraceDetail(optionalParams);

        if (traceDetail) {
            trace += `\nDetail: ${traceDetail}`;
        }

        return trace;
    }

    private getTraceDetail(optionalParams?: any[]): string | undefined {
        if (!optionalParams || optionalParams.length === 0) {
            return undefined;
        }

        const traceDetail = optionalParams
            .map(this.formatTraceDetailParam)
            .filter(detail => detail !== '{}');

        return traceDetail.length > 0
            ? this.formatTraceDetail(traceDetail)
            : undefined;
    }

    private formatTraceDetailParam(param: any) {
        if (param instanceof Response) {
            const resp = param as Response;
            return `URL: '${resp.url}', Status: ${resp.status}`;
        } else if (param instanceof Error) {
            return `Stack: ${param.stack}`;
        }

        return JSON.stringify(param);
    }

    private formatTraceDetail(traceDetail: string[]): string {
        return traceDetail
            .map((detail, idx) => `\n${idx}: ${detail}`)
            .join('');
    }

    private getFormattedTraceLevel(traceLevel: TraceLevel): string {
        return traceLevel.substring(0, BaseTracer.traceLevelLength).padEnd(BaseTracer.traceLevelLength);
    }

    private getFormattedSessionId(): string {
        return getSessionId().substring(0, 8);
    }
}